<template>
  <modal
    :show="isVisible"
    :title="modalTitle"
    size="medium"
    elem-id="pdv-enviar-whatsapp"
    data-testid="pdv-enviar-whatsapp-modal"
    :close-label="t('Fechar')"
    close-test-id="pdv-enviar-whatsapp-modal-close-btn"
    :save-label="t('Enviar WhatsApp')"
    save-icon="pi pi-whatsapp"
    save-test-id="pdv-enviar-whatsapp-modal-send-btn"
    :save-disabled-fn="saveDisabledFn"
    :save-fn="handleSend"
    @close="handleClose"
    @dismiss="handleClose"
  >
    <div v-if="isVisible" class="whatsapp-modal-body" data-testid="pdv-enviar-whatsapp-body">
      <!-- Client info -->
      <div class="client-info">
        <i class="pi pi-user"></i>
        <span class="client-name">{{ payload.clienteNome }}</span>
        <span v-if="payload.clienteTelefone" class="client-phone">{{ payload.clienteTelefone }}</span>
      </div>

      <!-- Message editor -->
      <div class="message-section">
        <p class="section-label">{{ t('Mensagem:') }}</p>
        <input-rich-text
          v-model="messageHtml"
          elem-id="pdv-enviar-whatsapp-message"
          data-testid="pdv-enviar-whatsapp-message-editor"
          :label="t('Mensagem WhatsApp')"
          :show-toolbar="true"
          :min-height="'120px'"
        />
        <button
          type="button"
          class="restore-btn"
          data-testid="pdv-enviar-whatsapp-restore-template-btn"
          @click="restoreTemplate"
        >
          {{ t('Restaurar template') }}
        </button>
      </div>
    </div>
  </modal>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, onBeforeUnmount } from 'vue';
import { useI18n } from 'vue-i18n';
import { eventBus } from '../../eventBus';
import { useClientePreCadastro } from '../../composables/useClientePreCadastro';
import { error as logError } from '../../utils/DebugLogger';
import { tenantConfig } from '../../services/tenantConfigService';

const { t } = useI18n();

// ─── Types ───────────────────────────────────────────────────────

interface WhatsAppPayload {
  tipo: 'pos_os' | 'producao' | 'saida' | 'pre_cadastro' | 'laudo_entrada' | 'laudo_saida';
  clienteId: number;
  clienteNome: string;
  clienteTelefone: string;
  isNewClient?: boolean;
  linkPreCadastro?: string;
  linkLaudo?: string;
  numeroOs?: string;
  /** so_order_item_id — when present, the send is recorded as a pickup
   *  notification for that item, so /pdv/entregas stops asking to "Avisar". */
  orderItemId?: number;
  dataPrevista?: string;
  servicoNome?: string;
  marcaModelo?: string;
  franquiaNome?: string;
}

// ─── State ───────────────────────────────────────────────────────

const isVisible = ref(false);
const isSending = ref(false);
const payload = ref<WhatsAppPayload>({
  tipo: 'pos_os',
  clienteId: 0,
  clienteNome: '',
  clienteTelefone: '',
});
const messageHtml = ref('');

// ─── Computed ────────────────────────────────────────────────────

const modalTitle = computed(() => {
  switch (payload.value.tipo) {
    case 'pos_os':
      return t('Enviar confirmação da OS');
    case 'producao':
      return t('Enviar aviso de produção');
    case 'saida':
      return t('Enviar aviso de retirada');
    case 'pre_cadastro':
      return t('Enviar link de pré-cadastro');
    case 'laudo_entrada':
      return t('Enviar laudo de entrada');
    case 'laudo_saida':
      return t('Enviar laudo de saída');
    default:
      return t('Enviar WhatsApp');
  }
});

const saveDisabledFn = (): boolean => {
  if (isSending.value) return true;
  const text = stripHtml(messageHtml.value).trim();
  return text.length === 0;
};

// ─── Helpers ─────────────────────────────────────────────────────

function escapeHtml(s: string): string {
  return s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
}

function stripHtml(html: string): string {
  const doc = new DOMParser().parseFromString(`<root>${html}</root>`, 'text/html');
  return (doc.querySelector('root')?.textContent ?? '').trim();
}

function walk(node: Node): string {
  if (node.nodeType === Node.TEXT_NODE) return node.textContent ?? '';
  if (!(node instanceof Element)) return '';
  const inner = Array.from(node.childNodes).map(walk).join('');
  const tag = node.tagName.toLowerCase();
  switch (tag) {
    case 'strong':
    case 'b':
      return `*${inner}*`;
    case 'em':
    case 'i':
      return `_${inner}_`;
    case 's':
    case 'strike':
      return `~${inner}~`;
    case 'li':
      return `• ${inner}\n`;
    case 'p':
      return `${inner}\n`;
    case 'br':
      return '\n';
    default:
      return inner;
  }
}

function htmlToWhatsappText(html: string): string {
  if (!html) return '';
  const doc = new DOMParser().parseFromString(`<root>${html}</root>`, 'text/html');
  return walk(doc.querySelector('root') as Element).trim();
}

// ─── Template builders ────────────────────────────────────────────

/**
 * Interpola UM slot de uma chave i18n.
 *
 * vue-i18n compila `{slot}` como named param. Enquanto a chave não tem tradução
 * registrada, `t(chave)` devolve a chave crua (formatFallbackMessages) e as
 * chaves sobrevivem; assim que existe tradução — e o `missing:
 * handleMissingTranslation` do main.ts registra a chave no PRIMEIRO uso, com
 * valor igual à própria chave — `t()` sem params troca o token por string VAZIA
 * e o nome do produto sumia da mensagem enviada ao cliente. Por isso o param
 * nomeado é obrigatório.
 *
 * Tradutor renomeando o token é real nesta base (`{star} estrelas` está como
 * `{estrela} estrelas` em pt-PT, `{page}` como `{página}`), e aí o param nomeado
 * também não casa. Descartadas: interpolação posicional `{0}` (a chave É a frase
 * pt-BR — trocar o token cria chave nova e órfã as traduções já registradas, e o
 * tradutor renomeia `{0}` do mesmo jeito) e simplesmente tolerar (mandar
 * "Recebemos seu  para revitalização!" para o cliente não é tolerável).
 * Escolhida a GUARDA: se o valor não sobreviveu à tradução, cai para a frase
 * pt-BR interpolada — mensagem em pt-BR COM o produto vale mais do que mensagem
 * traduzida SEM o produto.
 */
function tSlot(key: string, slot: string, value: string): string {
  const translated = t(key, { [slot]: value });
  if (value && !translated.includes(value)) {
    return key.replace(`{${slot}}`, () => value);
  }
  return translated;
}

/**
 * Quem assina a mensagem: a franquia, quando quem abriu o modal a informou;
 * senão a marca do tenant. Nenhuma tela informa a franquia hoje, e o fixo
 * 'Franchise Factory' chegava ao cliente final (NS 2026-0088).
 */
function assinatura(p: WhatsAppPayload): string {
  return p.franquiaNome || tenantConfig.get<string>('tenant_name') || 'Franchise Factory';
}

function buildPosOsNewTemplate(): string {
  const p = payload.value;
  const name = escapeHtml(p.clienteNome);
  const os = escapeHtml(p.numeroOs ?? '-');
  const previsao = escapeHtml(p.dataPrevista ?? '');
  const link = escapeHtml(p.linkPreCadastro ?? '');
  const modelo = escapeHtml(p.marcaModelo ?? t('item'));

  return [
    `<p>🖐️ ${t('Olá')}, ${name}!</p>`,
    `<p></p>`,
    `<p>${tSlot('Recebemos seu {modelo} para revitalização!', 'modelo', modelo)}</p>`,
    `<p></p>`,
    `<p>📋 OS: #${os}</p>`,
    previsao ? `<p>📅 ${t('Previsão')}: ${previsao}</p>` : '',
    `<p></p>`,
    `<p>💡 ${t('Para agilizar seu atendimento, complete seu cadastro aqui:')}</p>`,
    `<p>${link}</p>`,
    `<p></p>`,
    `<p>✅ ${t('Rápido e prático!')} 📱</p>`,
    `<p>${t('Equipe')} ${escapeHtml(assinatura(p))}</p>`,
  ].join('');
}

function buildPosOsExistingTemplate(): string {
  const p = payload.value;
  const name = escapeHtml(p.clienteNome);
  const os = escapeHtml(p.numeroOs ?? '-');
  const previsao = escapeHtml(p.dataPrevista ?? '');
  const modelo = escapeHtml(p.marcaModelo ?? t('item'));

  return [
    `<p>🖐️ ${t('Olá')}, ${name}!</p>`,
    `<p></p>`,
    `<p>${tSlot('Recebemos seu {modelo} para revitalização!', 'modelo', modelo)}</p>`,
    `<p></p>`,
    `<p>📋 OS: #${os}</p>`,
    previsao ? `<p>📅 ${t('Previsão')}: ${previsao}</p>` : '',
    `<p></p>`,
    `<p>${t('Qualquer novidade avisamos por aqui!')}</p>`,
    `<p>${t('Equipe')} ${escapeHtml(assinatura(p))}</p>`,
  ].join('');
}

function buildProducaoTemplate(): string {
  const p = payload.value;
  const name = escapeHtml(p.clienteNome);
  const os = escapeHtml(p.numeroOs ?? '-');
  const servico = escapeHtml(p.servicoNome ?? t('item'));
  const modelo = escapeHtml(p.marcaModelo ?? '');

  return [
    `<p>⚙️ ${name}, ${tSlot('seu {item} já está em produção!', 'item', modelo || servico)}</p>`,
    `<p></p>`,
    `<p>OS: #${os}</p>`,
    `<p>${t('Status')}: ${t('Em andamento')} 🚀</p>`,
    `<p></p>`,
    `<p>${t('Estamos cuidando de cada detalhe!')}</p>`,
    `<p>${t('Equipe')} ${escapeHtml(assinatura(p))}</p>`,
  ].join('');
}

function buildSaidaTemplate(): string {
  const p = payload.value;
  const name = escapeHtml(p.clienteNome);
  const os = escapeHtml(p.numeroOs ?? '-');
  const servico = escapeHtml(p.servicoNome ?? t('item'));
  const modelo = escapeHtml(p.marcaModelo ?? '');

  return [
    `<p>✅ ${t('Prontinho')}, ${name}!</p>`,
    `<p></p>`,
    `<p>${tSlot('Seu {item} está finalizado!', 'item', modelo || servico)}</p>`,
    `<p>OS: #${os}</p>`,
    `<p></p>`,
    `<p>📍 ${t('Retire na')} ${escapeHtml(p.franquiaNome ?? t('loja'))}</p>`,
    `<p></p>`,
    `<p>${t('Se preferir entrega, é só avisar!')} 🚚</p>`,
    `<p>${t('Equipe')} ${escapeHtml(assinatura(p))}</p>`,
  ].join('');
}

function buildPreCadastroTemplate(): string {
  const p = payload.value;
  const name = escapeHtml(p.clienteNome);
  const link = escapeHtml(p.linkPreCadastro ?? '');

  return [
    `<p>🖐️ ${t('Olá')}, ${name}!</p>`,
    `<p></p>`,
    `<p>${t('Seja bem-vindo(a)!')}</p>`,
    `<p></p>`,
    `<p>💡 ${t('Para agilizar seu atendimento, complete seu cadastro aqui:')}</p>`,
    `<p>${link}</p>`,
    `<p></p>`,
    `<p>✅ ${t('Rápido e prático!')} 📱</p>`,
    `<p>${t('Equipe')} ${escapeHtml(assinatura(p))}</p>`,
  ].join('');
}

function buildLaudoTemplate(): string {
  const p = payload.value;
  const name = escapeHtml(p.clienteNome);
  const link = escapeHtml(p.linkLaudo ?? '');

  return [
    `<p>🖐️ ${t('Olá')}, ${name}!</p>`,
    `<p></p>`,
    `<p>🔎 ${t('Confira o laudo do seu item:')}</p>`,
    `<p>${link}</p>`,
    `<p></p>`,
    `<p>${t('Equipe')} ${escapeHtml(assinatura(p))}</p>`,
  ].join('');
}

function buildMessageHtml(): string {
  const p = payload.value;
  switch (p.tipo) {
    case 'pos_os':
      return p.isNewClient ? buildPosOsNewTemplate() : buildPosOsExistingTemplate();
    case 'producao':
      return buildProducaoTemplate();
    case 'saida':
      return buildSaidaTemplate();
    case 'pre_cadastro':
      return buildPreCadastroTemplate();
    case 'laudo_entrada':
    case 'laudo_saida':
      return buildLaudoTemplate();
    default:
      return '';
  }
}

function restoreTemplate(): void {
  messageHtml.value = buildMessageHtml();
}

// ─── Send ────────────────────────────────────────────────────────

async function handleSend(): Promise<void> {
  if (saveDisabledFn()) return;

  const phone = (payload.value.clienteTelefone ?? '').replace(/\D/g, '');
  const waMessage = htmlToWhatsappText(messageHtml.value);

  if (!phone) {
      eventBus.emit("globalMessage.warning", t("Cliente sem telefone — não foi possível enviar"))
      return
    }
    if (waMessage) {
    window.open(
      // api.whatsapp.com/send direto — o redirect do wa.me corrompe os emojis
      // (UTF-8 → �) ao repassar o ?text. Enviando direto, o encodeURIComponent
      // preserva os emojis corretamente.
      `https://api.whatsapp.com/send/?phone=${phone}&text=${encodeURIComponent(waMessage)}`,
      '_blank',
      'noopener,noreferrer',
    );
  }

  isSending.value = true;
  try {
    // Post audit notification (best-effort)
    await postAuditNote(waMessage);
    eventBus.emit('globalMessage.success', t('Mensagem enviada'));
    handleClose();
  } catch (err) {
    logError('PDVEnviarWhatsAppModal', 'handleSend', 'audit notify failed', err);
    // Still close — the message already opened in WhatsApp
    handleClose();
  } finally {
    isSending.value = false;
  }
}

async function postAuditNote(note: string): Promise<void> {
  const { postPickupNotification } = useClientePreCadastro();
  const orderItemId = payload.value.orderItemId;
  await postPickupNotification(note, orderItemId);
  if (orderItemId) {
    // Same signal PDVEntregasNotifyModal emits: the deliveries screen and the
    // item detail reload their "Avisado há N dias" column.
    eventBus.emit('pdv-entregas.notifications.sent');
  }
}

// ─── Open / Close ────────────────────────────────────────────────

function handleOpen(newPayload: Partial<WhatsAppPayload> & Pick<WhatsAppPayload, 'tipo'>): void {
  payload.value = {
    tipo: newPayload.tipo,
    clienteId: newPayload.clienteId ?? 0,
    clienteNome: newPayload.clienteNome ?? '',
    clienteTelefone: newPayload.clienteTelefone ?? '',
    isNewClient: newPayload.isNewClient ?? false,
    linkPreCadastro: newPayload.linkPreCadastro,
    linkLaudo: newPayload.linkLaudo,
    numeroOs: newPayload.numeroOs,
    orderItemId: newPayload.orderItemId,
    dataPrevista: newPayload.dataPrevista,
    servicoNome: newPayload.servicoNome,
    marcaModelo: newPayload.marcaModelo,
    franquiaNome: newPayload.franquiaNome,
  };
  isVisible.value = true;
  messageHtml.value = buildMessageHtml();
}

function handleClose(): void {
  isVisible.value = false;
}

// ─── Lifecycle ───────────────────────────────────────────────────

const onOpenEvent = (p?: WhatsAppPayload): void => {
  if (!p) return;
  handleOpen(p);
};

onMounted(() => {
  eventBus.on('modal.pdv-enviar-whatsapp.open', onOpenEvent);
  eventBus.on('modal.pdv-enviar-whatsapp.close', handleClose);
});

onBeforeUnmount(() => {
  eventBus.off('modal.pdv-enviar-whatsapp.open', onOpenEvent);
  eventBus.off('modal.pdv-enviar-whatsapp.close', handleClose);
});
</script>

<style scoped>
.whatsapp-modal-body {
  padding: 8px 0;
}

.client-info {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 0;
  border-bottom: 1px solid var(--p-surface-200, #e5e7eb);
  margin-bottom: 16px;
}

.client-info .pi-user {
  font-size: 1.2rem;
  color: var(--p-primary-color, #3b82f6);
}

.client-name {
  font-weight: 600;
  color: var(--p-text-color, #374151);
}

.client-phone {
  color: var(--p-text-muted-color, #6b7280);
  font-size: 0.9rem;
}

.section-label {
  margin: 0 0 8px 0;
  font-weight: 600;
  color: var(--p-text-color, #374151);
}

.message-section {
  margin-top: 8px;
}

.restore-btn {
  margin-top: 8px;
  padding: 4px 12px;
  font-size: 0.8rem;
  background: transparent;
  border: 1px solid var(--p-surface-400, #9ca3af);
  border-radius: 4px;
  cursor: pointer;
  color: var(--p-text-secondary-color, #6b7280);
  transition: background 0.15s;
}

.restore-btn:hover {
  background: var(--p-surface-100, #f3f4f6);
}
</style>
